// Registry-driven form. Renders one input per ParamSpec. The form
// returns a record where:
//   - String fields → string
//   - U64 / U128 fields → string (preserves big ints over JSON)
//   - chain_kind → string (PascalCase ChainKind variant)
//   - account_id / btc_tx_hash / near_tx_hash / omni_address → string
//
// The backend's typed `parse` deserialises this record into the
// matching params struct, so the field names + types here must match
// `shared/src/actions/params.rs`.

import { useState } from "react";
import type { ChangeEvent } from "react";
import type { ChainKindName, ParamSpec, ParamKindWire } from "@/types/dto";
import { paramKind } from "@/types/dto";
import type { Network } from "@/config/network";

export type FormBody = Record<string, unknown>;

/** Integer-width ceilings for U64 / U128 form fields. Matches the Rust
 *  `u64` / `u128` the backend params deserialize into. */
const U64_MAX = 2n ** 64n - 1n;
const U128_MAX = 2n ** 128n - 1n;

interface Props {
  params: ParamSpec[];
  /** Pre-filled values per param name. */
  initial?: Record<string, string>;
  /** Network-aware placeholder substitution: e.g. `wrap.testnet` →
   *  `wrap.near` when network=mainnet. Applied to `placeholder` only. */
  placeholderHook?: (raw: string) => string;
  /** Active network — gates mainnet-only chains out of chain dropdowns. */
  network?: Network;
  /** Called every time a field changes. The parent owns the live body. */
  onChange: (body: FormBody, error: string | null) => void;
}

export function ActionForm({ params, initial, placeholderHook, network, onChange }: Props) {
  const [values, setValues] = useState<Record<string, string>>(() => ({ ...(initial ?? {}) }));

  function update(name: string, value: string) {
    const next = { ...values, [name]: value };
    setValues(next);
    const { body, error } = parseValues(params, next);
    onChange(body, error);
  }

  return (
    <div className="space-y-3" data-testid="action-form">
      {params.map((spec) => {
        const kind = paramKind(spec.kind);
        const value = values[spec.name] ?? "";
        const placeholder = placeholderHook && spec.placeholder ? placeholderHook(spec.placeholder) : spec.placeholder;
        const id = `field-${spec.name}`;
        return (
          <div key={spec.name}>
            <label htmlFor={id} className="block text-xs font-medium text-slate-700 dark:text-slate-300">
              {spec.label}
              {!spec.required && <span className="ml-1 text-slate-500">(optional)</span>}
            </label>
            <FieldInput
              id={id}
              kind={spec.kind}
              allowedChains={spec.allowed_chains}
              network={network}
              value={value}
              placeholder={placeholder}
              onChange={(v) => update(spec.name, v)}
            />
            {spec.help && (
              <p className="mt-1 text-[11px] text-slate-500 leading-relaxed">{spec.help}</p>
            )}
            {kind.type === "u64" || kind.type === "u128" ? (
              <p className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-600">
                {kind.type.toUpperCase()} — decimal string, no scientific notation
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

const DEFAULT_CHAIN_OPTIONS: ChainKindName[] = [
  "Near",
  "Eth",
  "Sol",
  "Arb",
  "Base",
  "Bnb",
  "Polygon",
  "Abs",
  "Btc",
  "Zcash",
  "Starknet",
  "Fogo",
  "Ton",
  "Sui",
];

/** Chains the omni-bridge only deploys on mainnet — hidden from chain
 *  dropdowns when the app is on testnet. Fogo has no testnet deployment. */
const MAINNET_ONLY_CHAINS: ChainKindName[] = ["Fogo"];

/** Options for a chain_kind dropdown: the action's `allowed_chains` (or the
 *  full set), minus chains not deployed on the active network. Exported for
 *  unit tests. */
export function chainOptions(
  allowedChains: ChainKindName[] | undefined,
  network?: Network,
): ChainKindName[] {
  const all = allowedChains && allowedChains.length > 0 ? allowedChains : DEFAULT_CHAIN_OPTIONS;
  return network === "testnet" ? all.filter((c) => !MAINNET_ONLY_CHAINS.includes(c)) : all;
}

function FieldInput(props: {
  id: string;
  kind: ParamKindWire;
  allowedChains?: ChainKindName[];
  network?: Network;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}) {
  const { id, kind, allowedChains, network, value, placeholder, onChange } = props;
  const k = paramKind(kind);
  const baseClass =
    "mt-1 block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-mono text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-near-green focus:outline-none";
  if (k.type === "chain_kind") {
    const options = chainOptions(allowedChains, network);
    return (
      <select
        id={id}
        value={value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) => onChange(e.target.value)}
        className={baseClass}
      >
        <option value="" disabled>
          — pick a chain —
        </option>
        {options.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      id={id}
      type="text"
      inputMode={k.type === "u64" || k.type === "u128" ? "numeric" : "text"}
      value={value}
      placeholder={placeholder}
      onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      className={baseClass}
      maxLength={k.type === "string" ? k.max_len : undefined}
    />
  );
}

/** Convert string-typed form state into the JSON body the backend
 *  expects. Validates U64 / U128 fields (decimal strings only, no
 *  hex / underscores / scientific notation, within integer width).
 *  Returns `error` non-null when a field is invalid; the parent disables
 *  submit. Exported for unit tests — this is the form's wire contract. */
export function parseValues(
  params: ParamSpec[],
  values: Record<string, string>,
): { body: FormBody; error: string | null } {
  const body: FormBody = {};
  for (const spec of params) {
    const raw = (values[spec.name] ?? "").trim();
    if (!raw) {
      if (spec.required) {
        return { body, error: `${spec.label} is required` };
      }
      continue;
    }
    const k = paramKind(spec.kind);
    if (k.type === "u64" || k.type === "u128") {
      if (!/^[0-9]+$/.test(raw)) {
        return { body, error: `${spec.label} must be a non-negative integer` };
      }
      // Reject values past the field's integer width here, inline, rather
      // than letting an over-range nonce/amount sail through to viem
      // (which throws "Number … is not in safe 64-bit unsigned integer
      // range") or to the contract (which rejects the signed tx on-chain).
      // BigInt is safe — `raw` is already `^[0-9]+$`.
      const max = k.type === "u64" ? U64_MAX : U128_MAX;
      if (BigInt(raw) > max) {
        return { body, error: `${spec.label} exceeds ${k.type.toUpperCase()} max (${max})` };
      }
      // Both U64 and U128 ALWAYS travel as decimal strings. This is the
      // canonical wire form: it matches the "decimal string" hint shown
      // under the field, avoids JS Number precision loss past 2^53, and
      // is what the backend expects — `origin_nonce` / `nonce` /
      // `fee_rate` are String-typed there, and the genuinely-numeric
      // fields (`vout`, `sign_index`) use a flexible deserializer that
      // accepts a string too. Sending a JS number here is what caused
      // `invalid type: integer 20122, expected a string` on small
      // String-typed nonces.
    }
    body[spec.name] = raw;
  }
  return { body, error: null };
}
